import { Route, Routes, Navigate, useParams } from 'react-router-dom';
import React, { Suspense, lazy } from 'react';
import {
	PrivateRoute,
	GuestOnlyRoute,
	NotFoundOrNotAllowedRedirect,
} from './components/RouteComponents';
import HomePage from './pages/OfflinePages/HomePage/HomePage';
import SignInSignUpPage from './pages/AuthenticationPages/SignUpPage/SignInSignUpPage';
import ResetPasswordPage from './pages/AuthenticationPages/ResetPasswordPage/ResetPasswordPage';
import UnauthorizedAccessPage from './pages/UnauthorizedAccess/UnauthorizedAccess';
import AppSumoActivateRedirect from './pages/Subscription/AppSumoActivateRedirect';
import MainDashboard from './components/Dashboard/MainDashboard';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import OutreachGate from './components/CRM/Outreach/OutreachGate';
import HarvestLeadsGate from './components/LeadGen/HarvestLeadsGate';

const InvoicesPage = lazy(() => import('./pages/Invoices/InvoicePage'));
const CreateAndUpdateInvoicePage = lazy(
	() => import('./pages/Invoices/CreateAndUpdateInvoicePage'),
);
const ContactsPage = lazy(() => import('./pages/Contacts/ContactsPage'));
const AnalyticsPage = lazy(
	() => import('./pages/GeneralAnalytics/AnalyticsPage'),
);
const ContactsImportPage = lazy(
	() => import('./pages/Contacts/ContactsImportPage'),
);
const ListDetailsPage = lazy(() => import('./pages/Lists/ListDetailsPage'));
const ListsPage = lazy(() => import('./pages/Lists/ListsPage'));
const MjmlBuilderPage = lazy(() => import('./pages/Builder/MjmlBuilderPage'));
const SimpleEmailBuilderPage = lazy(
	() => import('./pages/Builder/SimpleEmailBuilderPage'),
);
const BuilderHubPage = lazy(() => import('./pages/Builder/BuilderHubPage'));
const LandingPagesHubPage = lazy(
	() => import('./pages/LandingPages/LandingPagesHubPage'),
);
const LandingPageBuilderPage = lazy(
	() => import('./pages/LandingPages/LandingPageBuilderPage'),
);
const FormsListPage = lazy(() => import('./pages/Forms/FormsListPage'));
const FormBuilderPage = lazy(() => import('./pages/Forms/FormBuilderPage'));
const PublicLandingPage = lazy(() =>
	import('./pages/PublicMarketing/PublicMarketingPages').then((mod) => ({
		default: mod.PublicLandingPage,
	})),
);
const PublicFormPage = lazy(() =>
	import('./pages/PublicMarketing/PublicMarketingPages').then((mod) => ({
		default: mod.PublicFormPage,
	})),
);
const PrivacyPage = lazy(() =>
	import('./pages/Legal/LegalPages').then((mod) => ({
		default: mod.PrivacyPage,
	})),
);
const TermsPage = lazy(() =>
	import('./pages/Legal/LegalPages').then((mod) => ({ default: mod.TermsPage })),
);
const CookiesPage = lazy(() =>
	import('./pages/Legal/LegalPages').then((mod) => ({
		default: mod.CookiesPage,
	})),
);
const TemplatesPage = lazy(() => import('./pages/Templates/TemplatesPage'));
const AudiencesPage = lazy(() => import('./pages/AudiencesPage/AudiencesPage'));
const EmailVerificationPage = lazy(
	() => import('./pages/Contacts/EmailVerificationPage'),
);
const CreateUpdateHtmlTemplate = lazy(
	() => import('./components/Templates/HTML_Templates/CreateUpdateHtmlTemplate'),
);
const UnifiedSettingsPage = lazy(
	() => import('./pages/Settings/UnifiedSettingsPage/UnifiedSettingsPage'),
);
const ViewInvoicePage = lazy(() => import('./pages/Invoices/ViewInvoicePage'));
const ClientsPage = lazy(() => import('./pages/Clients/ClientsPage'));
const ViewClientDetailsPage = lazy(
	() => import('./pages/Clients/ViewClientDetailsPage'),
);
const AddClientPage = lazy(() => import('./pages/Clients/AddClientPage'));
const EmployeePage = lazy(() => import('./pages/Employee/EmployeePage'));
const EmployeeProfilePage = lazy(
	() => import('./pages/Employee/EmployeeProfilePage'),
);
const InvoiceManagementPage = lazy(
	() =>
		import('./pages/Invoices/InvoiceFinancialAnalytics/InvoiceFinancialAnalyticsPage'),
);
const WordPressConnectedSiteDetailsPage = lazy(
	() =>
		import('./pages/Websites/Integrations/Wordpress/WpConnectedSiteDetailsPage'),
);
const BlogsmithRevisionsHubPage = lazy(
	() => import('./pages/Websites/BlogsmithRevisionsHubPage'),
);
const SiteBlogsmithRevisionsPage = lazy(
	() =>
		import('./pages/Websites/Integrations/Wordpress/SiteWorkspace/SiteBlogsmithRevisionsPage'),
);
const StaffAiPostDetailPage = lazy(
	() => import('./pages/Websites/Integrations/Wordpress/StaffAiPostDetailPage'),
);
const LeadsPage = lazy(() => import('./pages/CRM/LeadsPage'));
const LeadDetailsPage = lazy(() => import('./pages/CRM/LeadDetailsPage'));
const OutreachTrackerPage = lazy(
	() => import('./pages/CRM/OutreachTrackerPage'),
);
const OutreachLeadPage = lazy(() => import('./pages/CRM/OutreachLeadPage'));
const PaymentSuccessfulPage = lazy(
	() => import('./pages/PaymentsPages/PaymentSuccessfulPage'),
);
const ProjectPage = lazy(() => import('./pages/ProjectPage/ProjectPage'));
const CreateUpdateProjectPage = lazy(
	() => import('./pages/ProjectPage/CreateUpdateProjectPage'),
);
const ProjectDetailsPage = lazy(
	() => import('./pages/ProjectPage/ProjectDetailsPage'),
);
const ProjectAnalyticsPage = lazy(
	() => import('./pages/ProjectPage/ProjectAnalyticsPage'),
);
const TaskDetailsPage = lazy(
	() => import('./pages/ProjectPage/TaskDetailsPage'),
);
const TodoAndTasks = lazy(
	() => import('./pages/CRM/TodoAndTasks/TodoAndTasksPage'),
);
const SubscribedCompanySubscriptionManagementPage = lazy(
	() =>
		import('./pages/PaymentsPages/SubscribedCompanySubscriptionManagementPage'),
);
const SubscribeToPlanFirstTimePage = lazy(
	() => import('./pages/Subscription/SubscribeToPlanFirstTimePage'),
);
const WordPressConnectedSiteUpdatePostPage = lazy(
	() =>
		import('./pages/Websites/Integrations/Wordpress/WpConnectedSiteCreateUpdatePostPage'),
);
const WordPressConnectedSiteAutomaticPostsSettingsPage = lazy(
	() =>
		import('./pages/Websites/Integrations/Wordpress/WordPressConnectedSiteAutomaticPostsSettingsPage'),
);
const AILearnWebsiteContentPage = lazy(
	() => import('./pages/AI/AILearnWebsiteContentPage'),
);
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const WebhooksPage = lazy(() => import('./pages/WebhooksPage'));
const CampaignFormPage = lazy(
	() => import('./pages/Campaigns/CampaignFormPage'),
);
const CampaignsPage = lazy(() => import('./pages/Campaigns/CampaignsPage'));
const CampaignAnalyticsPage = lazy(
	() => import('./pages/Campaigns/CampaignAnalyticsPage'),
);
const ScheduledPostsCalendarPage = lazy(
	() =>
		import('./pages/Websites/Integrations/Wordpress/ScheduledPostsCalendarPage'),
);
const MySupportPage = lazy(() => import('./pages/Support/MySupportPage'));
const AutomationsPage = lazy(
	() => import('./pages/Automations/AutomationsPage'),
);
const LeadGenPage = lazy(() => import('./pages/LeadGen/LeadGenPage'));
const OutboundPipelinePage = lazy(
	() => import('./pages/Automations/OutboundPipelinePage'),
);
const AutomationBuilderPage = lazy(
	() => import('./pages/Automations/AutomationBuilderPage'),
);
const WebsiteConnections = lazy(
	() => import('./pages/Websites/WebsiteConnections'),
);

const Lazy = ({ children }) => (
	<Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
);

const LegacyBuilderRedirect = () => {
	const { id } = useParams();
	return <Navigate to={`/builder/email/${id}`} replace />;
};

const AppRoutes = () => {
	return (
		<Lazy>
			<Routes>
				{/* Marketing home at root; legacy /home → / */}
				<Route
					path='/'
					element={
						<GuestOnlyRoute>
							<HomePage />
						</GuestOnlyRoute>
					}
				/>
				<Route path='/home' element={<Navigate to='/' replace />} />
				<Route
					path='/signup'
					element={
						<GuestOnlyRoute>
							<SignInSignUpPage />
						</GuestOnlyRoute>
					}
				/>
				<Route
					path='/auth'
					element={
						<GuestOnlyRoute>
							<SignInSignUpPage />
						</GuestOnlyRoute>
					}
				/>
				{/* Legacy AppSumo activate URL → signup */}
				<Route path='/appsumo/activate' element={<AppSumoActivateRedirect />} />
				<Route path='*' element={<NotFoundOrNotAllowedRedirect />} />
				<Route
					path='/my-support'
					element={
						<PrivateRoute
							pageTitle='My Support'
							pageDescription='View and manage your support tickets'
							allowedRoles={['Company Admin', 'Employee']}>
							<MySupportPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/automations'
					element={
						<PrivateRoute
							pageTitle='Automations'
							pageDescription='Create and manage automated workflows'
							allowedRoles={['Company Admin', 'Employee']}>
							<AutomationsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/lead-gen'
					element={
						<PrivateRoute
							pageTitle='Lead database'
							pageDescription='Browse the shared UK business directory and add contacts to outreach'
							allowedRoles={['Company Admin', 'Employee']}>
							<HarvestLeadsGate>
								<LeadGenPage />
							</HarvestLeadsGate>
						</PrivateRoute>
					}
				/>
				<Route
					path='/lead-gen/harvest'
					element={<Navigate to='/lead-gen' replace />}
				/>
				<Route
					path='/automations/create'
					element={
						<PrivateRoute
							pageTitle='Automation Builder'
							pageDescription='Design your outbound workflow visually'
							allowedRoles={['Company Admin', 'Employee']}>
							<AutomationBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/automations/edit/:id'
					element={
						<PrivateRoute
							pageTitle='Edit Automation'
							pageDescription='Update your outbound workflow'
							allowedRoles={['Company Admin', 'Employee']}>
							<AutomationBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/automations/outbound/:id'
					element={
						<PrivateRoute
							pageTitle='Outbound Pipeline'
							pageDescription='Track outbound sequence stages and replies'
							allowedRoles={['Company Admin', 'Employee']}>
							<OutboundPipelinePage />
						</PrivateRoute>
					}
				/>
				{/* START CAMPAIGN ROUTES */}
				<Route
					path='/campaigns'
					element={
						<PrivateRoute
							pageTitle='Campaign Management'
							pageDescription='Create, manage, and analyze email campaigns.'
							allowedRoles={['Company Admin', 'Employee']}>
							<CampaignsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/campaigns/create'
					element={
						<PrivateRoute
							pageTitle='Create Campaign'
							pageDescription='Build a new email marketing campaign'
							allowedRoles={['Company Admin', 'Employee']}>
							<CampaignFormPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/campaigns/:id/edit'
					element={
						<PrivateRoute
							pageTitle='Edit Campaign'
							pageDescription='Update campaign details and settings'
							allowedRoles={['Company Admin', 'Employee']}>
							<CampaignFormPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/campaigns/:id/analytics'
					element={
						<PrivateRoute
							pageTitle='Campaign Analytics'
							pageDescription='Track campaign performance and engagement'
							allowedRoles={['Company Admin', 'Employee']}>
							<CampaignAnalyticsPage />
						</PrivateRoute>
					}
				/>
				{/* END CAMPAIGN ROUTES */}
				<Route
					path='/integrations'
					element={
						<PrivateRoute
							pageTitle='Integrations Management'
							pageDescription='Connect third-party services and tools'
							allowedRoles={['Company Admin', 'Employee']}>
							<IntegrationsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/webhooks'
					element={
						<PrivateRoute
							pageTitle='Webhooks Management'
							pageDescription='Configure webhook endpoints and events'
							allowedRoles={['Company Admin', 'Employee']}>
							<WebhooksPage />
						</PrivateRoute>
					}
				/>
				{/* END */}
				<Route
					path='/dashboard/company'
					element={
						<PrivateRoute
							pageTitle='Dashboard - Company Admin'
							pageDescription='Your business overview at a glance'
							allowedRoles={['Company Admin']}>
							<MainDashboard />
						</PrivateRoute>
					}
				/>
				<Route
					path='/dashboard/employee'
					element={
						<PrivateRoute
							pageTitle='Dashboard - Employee'
							pageDescription='Your work summary and tasks'
							allowedRoles={['Employee']}>
							<MainDashboard />
						</PrivateRoute>
					}
				/>
				<Route
					path='/payment-success'
					element={
						<PrivateRoute
							pageTitle='Payment Successful Page'
							pageDescription='Your payment has been processed successfully'
							allowedRoles={['Company Admin', 'Employee']}>
							<PaymentSuccessfulPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/manage-subscription'
					element={
						<PrivateRoute
							pageTitle='Manage Subscription'
							pageDescription='Update your plan and billing settings'
							allowedRoles={['Company Admin', 'Employee']}>
							<SubscribedCompanySubscriptionManagementPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/subscribe-to-plan'
					element={
						<PrivateRoute
							pageTitle='Subscribe To Plan'
							pageDescription='Choose the right plan for your business'
							allowedRoles={['Company Admin', 'Employee']}>
							<SubscribeToPlanFirstTimePage />
						</PrivateRoute>
					}
				/>
				{/* CRM routes 'leads */}
				<Route
					path='/leads'
					element={
						<PrivateRoute
							pageTitle='Leads'
							pageDescription='Track and convert potential customers'
							allowedRoles={['Company Admin', 'Employee']}>
							<LeadsPage />
						</PrivateRoute>
					}
				/>
				{/* CRM routes 'leads */}
				<Route
					path='/leads/:id'
					element={
						<PrivateRoute
							pageTitle='Lead Details'
							pageDescription='View lead information and follow-ups'
							allowedRoles={['Company Admin', 'Employee']}>
							<LeadDetailsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/outreach-tracker'
					element={
						<PrivateRoute
							pageTitle='Outreach tracker'
							pageDescription='Track one-to-one outreach and follow-ups'
							allowedRoles={['Company Admin', 'Employee']}>
							<OutreachGate>
								<OutreachTrackerPage />
							</OutreachGate>
						</PrivateRoute>
					}
				/>
				<Route
					path='/outreach-tracker/:id'
					element={
						<PrivateRoute
							pageTitle='Outreach company'
							pageDescription='Email, contacts and timeline for one company'
							allowedRoles={['Company Admin', 'Employee']}>
							<OutreachGate>
								<OutreachLeadPage />
							</OutreachGate>
						</PrivateRoute>
					}
				/>
				<Route
					path='/todos-and-tasks'
					element={
						<PrivateRoute
							pageTitle='Todos and Tasks'
							pageDescription='Manage your to-dos and assignments'
							allowedRoles={['Company Admin', 'Employee']}>
							<TodoAndTasks />
						</PrivateRoute>
					}
				/>
				{/* Clients */}
				<Route
					path='/clients'
					element={
						<PrivateRoute
							pageTitle="Clients' Management"
							pageDescription='Manage your client relationships'>
							<ClientsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/clients/add'
					element={
						<PrivateRoute
							pageTitle='Add Client'
							pageDescription='Add a new client to your database'
							allowedRoles={['Company Admin', 'Employee']}>
							<AddClientPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/clients/view/:id'
					element={
						<PrivateRoute
							pageTitle='Viewing a client'
							pageDescription='View complete client information'>
							<ViewClientDetailsPage />
						</PrivateRoute>
					}
				/>
				{/* Invoices */}
				<Route
					path='/invoices'
					element={
						<PrivateRoute
							pageTitle='Invoices Overview'
							pageDescription='View and manage all your invoices'
							allowedRoles={['Company Admin', 'Employee']}>
							<InvoicesPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/invoices/create'
					element={
						<PrivateRoute
							pageTitle='Create an invoice'
							pageDescription='Create a new invoice for your client'
							allowedRoles={['Company Admin', 'Employee']}>
							<CreateAndUpdateInvoicePage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/invoice/edit/:id'
					element={
						<PrivateRoute
							pageTitle='Update an invoice'
							pageDescription='Edit invoice details and line items'
							allowedRoles={['Company Admin', 'Employee']}>
							<CreateAndUpdateInvoicePage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/invoice/view/:id'
					element={
						<PrivateRoute
							pageTitle='Viewing an invoice'
							pageDescription='Review invoice details and status'
							allowedRoles={['Company Admin', 'Employee']}>
							<ViewInvoicePage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/invoice/full-financial-analytics'
					element={
						<PrivateRoute
							pageTitle='Financial analytics'
							pageDescription='Track revenue and financial performance'
							allowedRoles={['Company Admin', 'Employee']}>
							<InvoiceManagementPage />
						</PrivateRoute>
					}
				/>
				{/* Projects */}
				<Route
					path='/projects'
					element={
						<PrivateRoute
							pageTitle='Manage Projects'
							pageDescription='Track and organize your projects'
							allowedRoles={['Company Admin', 'Employee']}>
							<ProjectPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/projects/add'
					element={
						<PrivateRoute
							pageTitle='Add a Project'
							pageDescription='Create a new project'
							allowedRoles={['Company Admin', 'Employee']}>
							<CreateUpdateProjectPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/projects/update/:id'
					// path="projects/:companyId/project/:id"
					element={
						<PrivateRoute
							pageTitle='Edit Project'
							pageDescription='Update project information'
							allowedRoles={['Company Admin', 'Employee']}>
							<CreateUpdateProjectPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/projects/details/:id'
					element={
						<PrivateRoute
							pageTitle='Project Details'
							pageDescription='View project progress and details'
							allowedRoles={['Company Admin', 'Employee']}>
							<ProjectDetailsPage />
						</PrivateRoute>
					}
				/>{' '}
				<Route
					path='/projects/details/:id/tasks/:taskId'
					element={
						<PrivateRoute
							pageTitle='Task Details'
							pageDescription='View and manage a task'
							allowedRoles={['Company Admin', 'Employee']}>
							<TaskDetailsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/projects/details/:id/tasks/:taskId/subtasks/:subtaskId'
					element={
						<PrivateRoute
							pageTitle='Subtask Details'
							pageDescription='View and manage a subtask'
							allowedRoles={['Company Admin', 'Employee']}>
							<TaskDetailsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/projects/analytics'
					element={
						<PrivateRoute
							pageTitle='Project Analytics'
							pageDescription='Portfolio-wide insights across all projects'
							allowedRoles={['Company Admin', 'Employee']}>
							<ProjectAnalyticsPage />
						</PrivateRoute>
					}
				/>{' '}
				{/* Employees */}
				<Route
					path='/employees'
					element={
						<PrivateRoute
							pageTitle='Employees Management'
							pageDescription='Manage your team members'
							allowedRoles={['Company Admin', 'Employee']}>
							<EmployeePage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/employee/view/:id'
					element={
						<PrivateRoute
							pageTitle='Employee Profile'
							pageDescription='View employee details and information'
							allowedRoles={['Company Admin', 'Employee']}>
							<EmployeeProfilePage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/analytics'
					element={
						<PrivateRoute
							pageTitle='Analytics'
							pageDescription='View comprehensive business analytics'
							allowedRoles={['Company Admin', 'Employee']}>
							<AnalyticsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/settings'
					element={
						<PrivateRoute
							pageTitle='Settings'
							pageDescription='Configure your system preferences'
							allowedRoles={['Company Admin', 'Employee']}>
							<UnifiedSettingsPage />
						</PrivateRoute>
					}
				/>{' '}
				<Route
					path='/website-connections/blogsmith-revisions'
					element={
						<PrivateRoute
							pageTitle='Blogsmith Revisions'
							pageDescription='Posts awaiting revision from clients'
							allowedRoles={['Company Admin', 'Employee']}>
							<BlogsmithRevisionsHubPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/website-connections'
					element={
						<PrivateRoute
							pageTitle='Website Connections'
							pageDescription='Manage your website connections'
							allowedRoles={['Company Admin', 'Employee']}>
							<WebsiteConnections />
						</PrivateRoute>
					}
				/>
				{/* <Route
				path='/website-connections'
				element={<Navigate to='/settings?section=website' replace />}
			/> */}
				{/* Contacts */}
				<Route
					path='/contacts'
					element={
						<PrivateRoute
							pageTitle='Contacts'
							pageDescription='Manage your contact database'
							allowedRoles={['Company Admin', 'Employee']}>
							<ContactsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/contacts/import/upload'
					element={
						<PrivateRoute
							pageTitle='Import Contacts'
							pageDescription='Bulk import contacts from file'
							allowedRoles={['Company Admin', 'Employee']}>
							<ContactsImportPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/audiences/lists/details/:id'
					element={
						<PrivateRoute
							pageTitle='List Details'
							pageDescription='View and manage list contacts'
							allowedRoles={['Company Admin', 'Employee']}>
							<ListDetailsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/audiences'
					element={
						<PrivateRoute
							pageTitle='Manage Client Audiences and Lists'
							pageDescription='Organize client in folders and their contacts into lists'
							allowedRoles={['Company Admin', 'Employee']}>
							<AudiencesPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/audiences/verify'
					element={
						<PrivateRoute
							pageTitle='Email List Verification'
							pageDescription='Validate email addresses and reduce bounce rates'
							allowedRoles={['Company Admin', 'Employee']}>
							<EmailVerificationPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/lists'
					element={
						<PrivateRoute
							pageTitle='Contact Lists'
							pageDescription='Manage all your contact lists'
							allowedRoles={['Company Admin', 'Employee']}>
							<ListsPage />
						</PrivateRoute>
					}
				/>
				<Route path='/privacy' element={<PrivacyPage />} />
				<Route path='/terms' element={<TermsPage />} />
				<Route path='/cookies' element={<CookiesPage />} />
				{/* Static copies are also served as /privacy.html etc. for crawlers */}
				<Route path='/privacy.html' element={<PrivacyPage />} />
				<Route path='/terms.html' element={<TermsPage />} />
				<Route path='/cookies.html' element={<CookiesPage />} />
				<Route path='/p/:slug' element={<PublicLandingPage />} />
				<Route path='/f/:slug' element={<PublicFormPage />} />
				<Route
					path='/builder'
					element={
						<PrivateRoute
							pageTitle='Builder'
							pageDescription='Choose email or landing page'
							allowedRoles={['Company Admin', 'Employee']}>
							<BuilderHubPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/builder/email'
					element={
						<PrivateRoute
							pageTitle='Email Builder'
							pageDescription='Design beautiful email templates'
							allowedRoles={['Company Admin', 'Employee']}>
							<MjmlBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/builder/email/simple'
					element={
						<PrivateRoute
							pageTitle='Simple Email'
							pageDescription='Write a personal inbox-style email'
							allowedRoles={['Company Admin', 'Employee']}>
							<SimpleEmailBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/builder/email/simple/:id'
					element={
						<PrivateRoute
							pageTitle='Simple Email'
							pageDescription='Edit your inbox-style email'
							allowedRoles={['Company Admin', 'Employee']}>
							<SimpleEmailBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/builder/email/:id'
					element={
						<PrivateRoute
							pageTitle='Email Builder'
							pageDescription='Edit your email template'
							allowedRoles={['Company Admin', 'Employee']}>
							<MjmlBuilderPage />
						</PrivateRoute>
					}
				/>
				{/* Legacy email builder URLs */}
				<Route path='/builder/:id' element={<LegacyBuilderRedirect />} />
				<Route
					path='/landing-pages'
					element={
						<PrivateRoute
							pageTitle='Landing pages'
							pageDescription='Pages, forms, and live analytics'
							allowedRoles={['Company Admin', 'Employee']}>
							<LandingPagesHubPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/landing-pages/:id'
					element={
						<PrivateRoute
							pageTitle='Landing page builder'
							pageDescription='Edit and publish your landing page'
							allowedRoles={['Company Admin', 'Employee']}>
							<LandingPageBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/forms'
					element={
						<PrivateRoute
							pageTitle='Forms'
							pageDescription='Marketing forms'
							allowedRoles={['Company Admin', 'Employee']}>
							<FormsListPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/forms/:id'
					element={
						<PrivateRoute
							pageTitle='Form builder'
							pageDescription='Configure marketing form'
							allowedRoles={['Company Admin', 'Employee']}>
							<FormBuilderPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/templates/create/html'
					element={
						<PrivateRoute
							pageTitle='Create hand coded HTML template'
							pageDescription='Build custom HTML email templates'
							allowedRoles={['Company Admin', 'Employee']}>
							<CreateUpdateHtmlTemplate />
						</PrivateRoute>
					}
				/>
				<Route
					path='/templates/update/html/:id'
					element={
						<PrivateRoute
							pageTitle='Update hand coded HTML template'
							pageDescription='Edit your custom HTML template'
							allowedRoles={['Company Admin', 'Employee']}>
							<CreateUpdateHtmlTemplate />
						</PrivateRoute>
					}
				/>
				<Route
					path='/templates'
					element={
						<PrivateRoute
							pageTitle='Templates'
							pageDescription='Browse and manage email templates'
							allowedRoles={['Company Admin', 'Employee']}>
							<TemplatesPage />
						</PrivateRoute>
					}
				/>
				<Route path='/reset-password/:token' element={<ResetPasswordPage />} />
				<Route
					path='/connected-sites/:connectionId/blogsmith-revisions'
					element={
						<PrivateRoute
							pageTitle='Site Blogsmith Revisions'
							pageDescription='Posts awaiting revision for this site'
							allowedRoles={['Company Admin', 'Employee']}>
							<SiteBlogsmithRevisionsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/connected-sites/:connectionId/ai-posts/:postId'
					element={
						<PrivateRoute
							pageTitle='Edit Blogsmith Post'
							pageDescription='Revise AI-generated content'
							allowedRoles={['Company Admin', 'Employee']}>
							<StaffAiPostDetailPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/connected-sites/:id'
					element={
						<PrivateRoute
							pageTitle='View details for a connected site'
							pageDescription='Manage your connected website'
							allowedRoles={['Company Admin', 'Employee']}>
							<WordPressConnectedSiteDetailsPage />
						</PrivateRoute>
					}
				/>
				<Route
					// path='/connected-sites/:id/posts/automatic/:postType/setup'
					path='/connected-sites/:id/generate-ai-content/recurring'
					element={
						<PrivateRoute
							pageTitle='Schedule ongoing posts'
							pageDescription='Set up AI-powered content automation'
							allowedRoles={['Company Admin', 'Employee']}>
							<WordPressConnectedSiteAutomaticPostsSettingsPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/connected-sites/:connectionId/generate-ai-content/one-off'
					element={
						<PrivateRoute
							pageTitle='Write one post'
							pageDescription='Write a new blog post'
							allowedRoles={['Company Admin', 'Employee']}>
							<WordPressConnectedSiteUpdatePostPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/connected-sites/:connectionId/posts/edit/:postType/:postId'
					element={
						<PrivateRoute
							pageTitle='Update Site Blog'
							pageDescription='Edit your blog post'
							allowedRoles={['Company Admin', 'Employee']}>
							<WordPressConnectedSiteUpdatePostPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/connected-sites/:id/ai-learn'
					element={
						<PrivateRoute
							pageTitle='AI: Learn About This Website'
							pageDescription='Train AI on your website content'
							allowedRoles={['Company Admin', 'Employee']}>
							<AILearnWebsiteContentPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/connected-sites/:id/scheduled-posts-calendar'
					element={
						<PrivateRoute
							pageTitle='Scheduled Posts Calendar'
							pageDescription='View and manage scheduled content'
							allowedRoles={['Company Admin', 'Employee']}>
							<ScheduledPostsCalendarPage />
						</PrivateRoute>
					}
				/>
				<Route
					path='/unauthorized'
					element={
						<PrivateRoute
							pageTitle='Unauthorized Access'
							pageDescription='You do not have access to this page'
							allowedRoles={['Company Admin', 'Employee']}>
							<UnauthorizedAccessPage />
						</PrivateRoute>
					}
				/>
			</Routes>
		</Lazy>
	);
};

export default AppRoutes;
